import { ROUTES } from '#/config/routes';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import {
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import {
  readAdjustmentDateRangeFilter,
  resolveAdjustmentDateFilterChipDisplay,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentDateListFilter';
import {
  STOCK_ADJUSTMENT_SOURCE_TYPE_FILTER_OPTIONS,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentSourceType';
import {
  STOCK_ADJUSTMENT_STATUS_FILTER_OPTIONS,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentStatus';
import { formatDateOrDash } from '#/utils/date';

const DATE_FILTER_PLACEHOLDER = '西元年/月/日';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

export function buildStockAdjustmentColumns({
  filters = {},
  onFilterChange,
  onAdjustmentDateRangeChange,
  canShowEdit,
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.stockAdjustmentDetail(row.id),
      getEditPath: (row) => ROUTES.stockAdjustmentEdit(row.id),
      canShowEdit,
    }),
    {
      id: 'adjustmentNo',
      header: '調整單號',
      accessorKey: 'adjustmentNo',
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'adjustmentNo',
        label: '調整單號',
        filterValue: filters.adjustmentNo,
        onChange: (value) => onFilterChange('adjustmentNo', value),
        placeholder: '搜尋調整單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <DocumentStatusBadge status={row.original.status} size="sm" />
      ),
      size: 96,
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: STOCK_ADJUSTMENT_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'adjustmentDate',
      header: '單據日',
      accessorKey: 'adjustmentDate',
      accessorFn: (row) => formatDateOrDash(row.adjustmentDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'adjustmentDateFrom',
        label: '單據日',
        mode: 'range',
        filterValue: readAdjustmentDateRangeFilter(filters),
        onChange: onAdjustmentDateRangeChange,
        placeholder: DATE_FILTER_PLACEHOLDER,
        resolveFilterDisplay: resolveAdjustmentDateFilterChipDisplay,
      }),
    },
    {
      id: 'issueReason',
      header: '開單原因',
      accessorKey: 'issueReason',
      accessorFn: (row) => displayDash(row.issueReason),
      size: 160,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'issueReason',
          label: '開單原因',
          filterValue: filters.issueReason,
          onChange: (value) => onFilterChange('issueReason', value),
          placeholder: '搜尋開單原因',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.issueReason)} />
      ),
    },
    {
      id: 'sourceType',
      header: '來源',
      accessorKey: 'sourceType',
      size: 100,
      enableSorting: false,
      enableColumnFilter: true,
      meta: createFixedMultiFilterMeta({
        key: 'sourceType',
        label: '來源',
        options: STOCK_ADJUSTMENT_SOURCE_TYPE_FILTER_OPTIONS,
        filterValue: filters.sourceType,
        onChange: (value) => onFilterChange('sourceType', value),
      }),
    },
    {
      id: 'remark',
      header: '備註',
      accessorKey: 'remark',
      accessorFn: (row) => displayDash(row.remark),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.remark)} />
      ),
    },
    {
      id: 'lineCount',
      header: '項目數',
      accessorKey: 'lineCount',
      size: 100,
      enableSorting: false,
      meta: { cellClassName: 'text-right' },
    },
  ];
}
